import type { IconType } from "react-icons";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import type { SerializedError } from "@reduxjs/toolkit";
import type { BEModulesClassNames } from "../utils/moduleList";
import type { Note, Option } from "./modulesInterfaces";
import { apiSlice, publicApiSlice } from "../app/apiSlice";

//generally we want to define interfaces in the component so that's private unless we reuse it
interface ProjectOption {
	name: string;
	value: string;
	id: number;
}
export enum Role {
	ADMIN = "Admin",
	REVIEWER = "Reviewer",
	ANALYST = "Analyst",
	RECEIVER = "Viewer",
}

interface Country extends Option {
	region?: Option;
	ipcc_region?: Option;
	gleam_region?: Option;
}

interface Project {
	id: number;
	climate: number;
	moisture: number;
	soil_type: number;
	country: number;
	gw_potential: number | null;
	status: number | null;
	area: ProjectArea | null;
	role: Role[];
	created_at: string;
	updated_at: string;
	date: Date;
	name: string;
	code: string;
	cost: number;
	funding_agency: string | null;
	executing_agency: string | null;
	implementation_years: number;
	capitalization_years: number;
	start_year_of_activities: number;
	last_year_of_accounting: number;
	soc_ref_t2: number | null;
	is_finalized: boolean;
	is_archived: boolean;
	map_data: string | null;
	is_public: boolean;
	/* sent by backend but not used in FE
	isLocked: boolean,
	lockedAt: string,
	lockUpdatedAt: string,
	lockedBy: number,
	*/
}

interface ProjectTotals {
	total_hectares: number | null;
	total_catch: {
		start: number | null;
		w: number | null;
		wo: number | null;
	} | null;
	total_livestock: {
		start: number | null;
		w: number | null;
		wo: number | null;
	} | null;
}

interface FastProject {
	id: number;
	name: string;
	country: string;
	updated_at: string;
	created_at: string;
	role: string[];
	is_finalized: boolean;
	is_archived: boolean;
	activity_count: string;
	module_count: string;
}

interface LandUseType {
	id: number
	module_types: number[]
	name: string
	is_active: boolean
	climates: number[]
	moistures: number[]
}

type ProjectArea = { lat: number; lng: number }[]

interface User {
	id: number;
	first_name: string;
	last_name: string;
	email: string;
	country: number;
	organization?: string;
	is_opted_out_of_emails?: boolean;
}

interface Activity {
	id: number;
	name: string;
	project?: number;
	change_rate: number | null;
	climate_t2?: number | null;
	moisture_t2?: number | null;
	soc_t2?: number | null;
	module_types: number[];
	owner: number;
	status: number | null;
	completion_percentage: number | null;
	description: string | null;
	cost: number | null;
	duration_t2?: number | null;
	start_year_t2?: number | null;
	last_year_of_accounting_t2?: number | null;
	capitalization_years?: number | null;
	is_b_intact?: boolean;
	created_at: string;
	updated_at: string;
}

interface FastActivity {
	id: number;
	name: string;
	module_types: number[] //ModuleType[];
	completion_percentage: number;
	is_b_intact: boolean;
}

interface Module {
	id: number;
	module_type: number;
	activity: Activity,
	status: number;
	endpoint_url: string;
	start_year: number;
	created_at: string;
	updated_at: string;
	area?: number;
}

interface ModuleType extends Option {
	id: number;
	is_luc: boolean;
	is_submodule: boolean;
	is_container: boolean;
	class_name: BEModulesClassNames;
}

interface TopbarStep {
	id: number; //FEModules
	name: string; //ex label
	path: string;
}

export enum ProjectNavigation {
	Description = "main.description",
	Activities = "activity.activities",
	Results = "main.results",
}

interface BuilderAction {
	id: Actions;
	label: string;
	icon: IconType;
	color: string;
	component: () => JSX.Element | null;
}

interface TimeOptions {
	start?: number | null;
	without: number | null;
	with: number | null;
}

interface ModuleInfo {
	name: string;
	area?: TimeOptions;
	totalEmission: TimeOptions;
}

interface BackendError {
	status: number;
	data: { detail: string };
}

export enum Actions {
	LiveUpdates = 0,
	Notes = 1,
	Comments = 2,
	Help = 3,
	Changes = 4,
	ProgressiveModules = 5,
	Map = 6,
	Documentation = 7,
	Definitions = 9,
	Attachments = 10,
}

type ErrorType = FetchBaseQueryError | SerializedError | undefined;
type ErrorFormat = {
	[key: string]: any;
	details: string[];
};

export interface GasType {
	name: "CO2" | "CH4" | "N2O";
}
interface BasePaginationResponse {
	count: number
	next: string
	previous: string
}

export type {
	LandUseType,
	ProjectOption,
	Project,
	TopbarStep,
	BuilderAction,
	ModuleInfo,
	BackendError,
	Activity,
	User,
	ErrorType,
	ModuleType,
	ErrorFormat,
	Module,
	ProjectArea,
	BasePaginationResponse,
	FastProject,
	FastActivity,
	Country,
	ProjectTotals
};
export type ApiSliceType = typeof apiSlice;
export type PublicApiSliceType = typeof publicApiSlice;
